<template>
  <div data-test-id="dynamic-data-program-builder">
    <!-- :loading reflects only the initial program load. A program SAVE must NOT feed
         `loading` (the Continue button shows its own spinner): ModuleBuilder replaces
         the step content with a spinner while loading, which would unmount the embedded
         rule-builder iframe and drop its unsaved edits. -->
    <ModuleBuilder
      title="Build a New Program"
      :show-intro="showIntro"
      :steps="steps"
      :on-next="handleNext"
      :on-back="canLeaveStep"
      :on-step-click="canLeaveStep"
      :loading="loadingProgram"
      :last-step-label="lastStepLabel"
      free-navigation
      :compact="false"
      :initial-step-key="initialStepKey"
      :active-substep-key-override="activeSubstepKeyOverride"
      @close="handleClose"
      @update:currentStepKey="currentStepKey = $event"
      @substep-click="handleSubstepClick"
      @builder-finished="builderFinished = true"
    >
      <template #intro-content>
        <div
          v-if="awaitingClientFeatureFlags"
          class="d-flex justify-center pa-6"
          data-test-id="client-feature-flags-loading"
        >
          <v-progress-circular indeterminate color="brandPrimary" width="2" size="25" />
        </div>
        <SelectIfClaimProgram v-else @select="onClaimProgramChoice" />
      </template>
      <template #step-content="{ currentStepKey }">
        <DetailsStep
          ref="detailsStep"
          v-show="currentStepKey === 'details'"
          :name="program.name"
          :description="program.description"
          :image="image"
          :image-u-r-l="program.imageURL"
          :image-alt-text="program.imageAltText"
          @update:name="mergeProgram({ name: $event, displayName: $event })"
          @update:description="mergeProgram({ description: $event })"
          @update:image="image = $event"
          @update:imageURL="mergeProgram({ imageURL: $event })"
          @update:imageAltText="mergeProgram({ imageAltText: $event })"
        />
        <TimelineStep
          v-if="!isClaimProgram"
          ref="timelineStep"
          v-show="currentStepKey === 'timeline'"
          :start-date="program.startDate"
          :end-date="program.endDate"
          @update:startDate="mergeProgram({ startDate: $event })"
          @update:endDate="mergeProgram({ endDate: $event })"
        />
        <ClaimTimelineStep
          v-else
          ref="timelineStep"
          v-show="currentStepKey === 'timeline'"
          :start-date="program.startDate"
          :end-date="program.endDate"
          :claims-open-date="program.claimsOpenDate"
          :claims-close-date="program.claimsCloseDate"
          :card-active-from="program.cardActiveFrom"
          :card-archive-after="program.cardArchiveAfter"
          :rule-calculation-window="program.ruleCalculationWindow"
          :payout-cadence="program.payoutCadence"
          :payout-end-offset-days="program.payoutEndOffsetDays"
          :payout-locked="payoutLocked"
          @update:startDate="mergeProgram({ startDate: $event })"
          @update:endDate="mergeProgram({ endDate: $event })"
          @update:claimsOpenDate="mergeProgram({ claimsOpenDate: $event })"
          @update:claimsCloseDate="mergeProgram({ claimsCloseDate: $event })"
          @update:cardActiveFrom="mergeProgram({ cardActiveFrom: $event })"
          @update:cardArchiveAfter="mergeProgram({ cardArchiveAfter: $event })"
          @update:ruleCalculationWindow="mergeProgram({ ruleCalculationWindow: $event })"
        />
        <AudienceStep
          ref="audienceStep"
          v-show="currentStepKey === 'audience'"
          :selected-groups="program.ProgramGroups"
          @update:selectedGroups="mergeProgram({ ProgramGroups: $event })"
        />
        <!-- Lazily mount the iframe the first time the user reaches this step
             (mounting is expensive), then keep it mounted and toggle visibility with
             v-show. Navigating away (Back / another step) must NOT unmount it — the
             user's unsaved rule edits live in the iframe's memory and would be lost. -->
        <div
          v-if="hasReachedRuleStep"
          v-show="currentStepKey === 'dynamic-rule-builder'"
          class="dynamic-rule-builder-step"
        >
          <!-- Continue stays active; the save round-trip (persistRuleSet, driven by
               ModuleBuilder's on-next) is the gate that blocks advancing/closing while
               the rules are invalid. Error status is surfaced inside the builder (its
               global-error banner + per-field errors); the host adds nothing of its own. -->
          <RuleSetBuilderIframe
            ref="ruleBuilder"
            fill-height
            :rule-set-id="program.ruleSetId"
            :name="program.name"
            :description="program.description"
            :valid-from="ruleSetValidFrom"
            :valid-to="ruleSetValidTo"
            :initial-global-filters="initialGlobalFilters"
            :constants="ruleSetConstants"
            @ready="ruleSetReady = true"
            @dirty-change="ruleSetDirty = $event"
            @validation-change="ruleSetValidation = $event"
          />
        </div>
        <RewardStep
          ref="rewardStep"
          v-show="currentStepKey === 'reward'"
          :budget-id="program.budgetId"
          :budgets="budgets"
          :loading-budgets="loadingBudgets"
          :can-select-budget="canSelectBudget"
          :payout-cadence="program.payoutCadence"
          :payout-end-offset-days="program.payoutEndOffsetDays"
          :payout-amount="program.payoutAmount"
          :program-end-date="program.endDate"
          :rule-calculation-window="program.ruleCalculationWindow"
          :payout-locked="payoutLocked"
          @update:budgetId="onRewardBudgetId"
          @update:payoutCadence="mergeProgram({ payoutCadence: $event })"
          @update:payoutEndOffsetDays="mergeProgram({ payoutEndOffsetDays: $event })"
          @update:payoutAmount="mergeProgram({ payoutAmount: $event })"
        />
        <ClaimFormStep
          v-if="isClaimProgram"
          ref="claimFormStep"
          v-show="currentStepKey === 'claim-form'"
          :form-id="selectedClaimFormId"
          :forms="claimForms"
          :loading-forms="loadingClaimForms"
          @update:formId="onClaimFormId"
        />
        <SummaryCardMetricsStep
          ref="summaryCardMetricsStep"
          v-show="currentStepKey === 'summary-card-metrics'"
          :rows="program.ProgramSummaryMetricRows"
          :metric-options="metricSelectItems"
          :inform-program-id="program.informProgramId"
          :metric-options-error="metricOptionsError"
          :metric-options-loaded="metricOptionsLoaded"
          :metric-options-reason="metricOptionsReason"
          @update:rows="mergeProgram({ ProgramSummaryMetricRows: $event })"
        />
        <DetailPageMetricsStep
          ref="detailPageMetricsStep"
          v-show="currentStepKey === 'detail-page-metrics'"
          :rows="program.ProgramMetricRows"
          :metric-options="metricSelectItems"
          :inform-program-id="program.informProgramId"
          :metric-options-error="metricOptionsError"
          :metric-options-loaded="metricOptionsLoaded"
          :metric-options-reason="metricOptionsReason"
          @update:rows="mergeProgram({ ProgramMetricRows: $event })"
        />
        <DetailPageTabsStep
          v-show="currentStepKey === 'detail-page-tabs'"
          ref="tabsStep"
          :tabs="program.DetailTabs"
          :program-id="program.programId"
          :program-name="program.displayName"
          :image-url="imagePreviewUrl"
          :start-date="program.startDate"
          :end-date="program.endDate"
          :metric-rows="program.ProgramMetricRows"
          :metric-projection="metricProjection"
          :payout-cadence="program.payoutCadence"
          :is-dollar-budget="isDollarBudget"
          :is-claim-program="isClaimProgram"
          @update:tabs="mergeProgram({ DetailTabs: $event })"
          @editor-closed="tabActiveSubstepKey = null"
        />
        <ProgramMessagesStep
          ref="messagesStep"
          v-show="currentStepKey === 'program-messages'"
          :messages="sortedMessages"
          :program-length="programLength"
          :program-start-date="program.startDate"
          :program-end-date="program.endDate"
          :added-default-messages="addedDefaultMessages"
          @update:messages="mergeProgram({ MessageDefinitions: $event })"
          @sheet-closed="messageActiveSubstepKey = null"
        />
        <ConfirmStep
          ref="confirmStep"
          v-show="currentStepKey === 'confirm'"
          :preview-mode="previewMode"
          :show-draft-toggle="canEditDraftMode"
          :start-date="program.startDate"
          :end-date="program.endDate"
          :selected-groups="program.ProgramGroups"
          :program-name="program.displayName || program.name"
          :image-url="imagePreviewUrl"
          :tabs="program.DetailTabs"
          :metric-rows="program.ProgramMetricRows"
          :metric-projection="metricProjection"
          :payout-cadence="program.payoutCadence"
          :is-dollar-budget="isDollarBudget"
          :is-claim-program="isClaimProgram"
          :claims-open-date="program.claimsOpenDate"
          :claims-close-date="program.claimsCloseDate"
          :rule-calculation-window="program.ruleCalculationWindow"
          :claim-form-name="selectedClaimFormName"
          :budget-name="currentBudgetLabel"
          :budget-balance="currentBudgetBalance"
          @update:previewMode="previewMode = $event"
        />
      </template>
      <template #preview="{ currentStepKey }">
        <SummaryCardMetricStepPreview
          v-show="currentStepKey === 'summary-card-metrics'"
          :name="program.displayName || program.name"
          :start-date="program.startDate"
          :end-date="program.endDate"
          :image-url="imagePreviewUrl"
          :payout-cadence="program.payoutCadence"
          :rows="program.ProgramSummaryMetricRows"
          :metric-projection="metricProjection"
          :is-dollar-budget="isDollarBudget"
        />
        <DetailPageMetricsStepPreview
          v-show="currentStepKey === 'detail-page-metrics'"
          :start-date="program.startDate"
          :end-date="program.endDate"
          :image-url="imagePreviewUrl"
          :rows="program.ProgramMetricRows"
          :metric-projection="metricProjection"
          :payout-cadence="program.payoutCadence"
          :is-dollar-budget="isDollarBudget"
        />
      </template>
      <template #success-content>
        <SuccessStep
          type="program"
          :action="['Incomplete', 'Preview'].includes(program.status) ? 'saved' : 'published'"
          @close="navigateToProgramAdmin"
        />
      </template>
    </ModuleBuilder>
    <ErrorDialogV2
      v-model="showCloseConfirm"
      title="Do you want to save any changes you made before you go?"
      confirm-button-text="Save"
      cancel-button-text="Don't save"
      show-cancel-button
      @confirm="handleCloseConfirmSave"
      @cancel="handleDiscardAndClose"
    />
    <ErrorDialogV2
      v-model="showMessagesErrorConfirm"
      title="You have messages with errors"
      message="One or more messages can't be saved because their send date falls outside the program dates. If you continue, these messages will be removed."
      confirm-button-text="Discard and Continue"
      cancel-button-text="Stay and fix"
      show-cancel-button
      @confirm="handleConfirmDiscardInvalidMessages"
      @cancel="handleStayAndFix"
    />
    <Modal v-model="savingProgram" persistent :show-close-button="false" max-width="320px">
      <div class="d-flex flex-column align-center ga-3">
        <RobinAnimation animation="backflip" :width="150" :height="150" />
        <Typography variant="subtitle-1">Saving program...</Typography>
      </div>
    </Modal>
  </div>
</template>

<script>
import ModuleBuilder from "@/components/module-builder/ModuleBuilder.vue";
import SelectIfClaimProgram from "./SelectIfClaimProgram.vue";
import DetailsStep from "./DetailsStep.vue";
import TimelineStep from "./TimelineStep.vue";
import ClaimTimelineStep from "./ClaimTimelineStep.vue";
import AudienceStep from "./AudienceStep.vue";
import RewardStep from "./RewardStep.vue";
import ClaimFormStep from "./ClaimFormStep.vue";
import SummaryCardMetricsStep from "./SummaryCardMetricsStep.vue";
import SummaryCardMetricStepPreview from "./SummaryCardMetricStepPreview.vue";
import DetailPageMetricsStep from "./DetailPageMetricsStep.vue";
import DetailPageMetricsStepPreview from "./DetailPageMetricsStepPreview.vue";
import ProgramMessagesStep from "./ProgramMessagesStep.vue";
import ConfirmStep from "./ConfirmStep.vue";
import RuleSetBuilderIframe from "@/components/rule-builder/RuleSetBuilderIframe.vue";
import ErrorDialogV2 from "@/components/ErrorDialogV2.vue";
import Modal from "@/components/generic/Modal.vue";
import RobinAnimation from "@/components/Robin.vue";
import Typography from "@/components/generic/Typography.vue";
import ProgramService from "@/services/ProgramService";
import ProgramServiceV3 from "@/services/ProgramServiceV3";
import GroupService from "@/services/GroupService";
import CommunicationService from "@/services/CommunicationService";
import BudgetService from "@/services/BudgetService";
import FormService from "@/services/FormService";
import moment from "moment";
import { mapState } from "vuex";
import permissionsMixin from "@/mixins/permissionsMixin";
import clientFeatureFlagsMixin from "@/mixins/clientFeatureFlags";
import SuccessStep from "@/components/module-builder/components/SuccessStep.vue";
import { getMessageBodyFromTemplate } from "@/shared_data/functions";
import DetailPageTabsStep from "./DetailPageTabsStep.vue";
import { sortMessagesBySendDate } from "./messageOrder";
import { buildSteps, applySteps } from "./BuilderStepConfig";
import {
  emptyMetricColumn,
  METRIC_SOURCE,
  toMetricSelectItems,
} from "./AddMetricSideSheet/metricOptions";
import { uploadEmbeddedContent, loadEmbeddedContent } from "@/utils/embeddedContentHelper";
import { getPayoutDate } from "@/utils/payoutDate";
import { v4 as uuidv4 } from "uuid";

const DDPB_CONTEXT = "dynamic_data_program_builder";
const DDPB_STATE_KEY = `${DDPB_CONTEXT}_state`;

// Fallback validTo tail past the payout when a program has no stored reward cutoff.
const RULE_CALCULATION_WINDOW_DAYS = 30;
const TIMELINE_VALUE_FORMAT = "YYYY-MM-DDTHH:mm";

const DEFAULT_MESSAGES = [
  {
    messageBody:
      "<p>Welcome to the program {{{user.firstName}}}! We're glad you're here! Let's get started!</p>",
    messageShortBody: "Get pumped!",
    messageSubject: "Welcome to the program!",
    id: -3,
    newMessage: true,
    numDaysAfterProgramStart: 0,
    numDaysToProgramEnd: null,
  },
  {
    messageBody:
      "<p>'{{{program.name}}}' program check-in! It's been 30 days since this program launched. If you've been putting it off, now is a good time to dive in!</p>",
    messageShortBody: "30 day check-in for the '{{{program.name}}}' program!",
    messageSubject: "It's not too late!",
    id: -2,
    newMessage: true,
    numDaysAfterProgramStart: 30,
    numDaysToProgramEnd: null,
  },
];

// A factory rather than a frozen const because the nested column objects would
// otherwise be shared across injections and edited in place.
const defaultMetricRow = () => ({
  rowId: -1,
  columnCount: 1,
  columns: [
    { ...emptyMetricColumn("BIG_NUMBER"), slotIndex: 0, metricSource: METRIC_SOURCE.EARNINGS },
  ],
});

const today = moment().format(TIMELINE_VALUE_FORMAT);

const INITIAL_PROGRAM_STATE = {
  programId: null,
  // DaaS rule set backing this dynamic program. Populated when the embedded builder
  // saves on the rule step (see persistRuleSet); persisted with the program so edit
  // mode can reload the builder with the existing rules.
  ruleSetId: null,
  // Links the Engage program to its Inform counterpart; minted on create only.
  informProgramId: null,
  name: "",
  displayName: "",
  description: "",
  startDate: today,
  endDate: null,
  // Claim-only. The timeline step configures these; non-claim saves null them.
  claimsOpenDate: today,
  claimsCloseDate: null,
  cardActiveFrom: null,
  cardArchiveAfter: null,
  ruleCalculationWindow: null,
  type: "dynamic-data",
  subtype: null,
  ProgramGroups: [],
  // Selected budget for the reward step. Persisted directly on the Program
  // record (Program.budgetId column) so create/update pass it through as-is.
  budgetId: null,
  // Nested budget from the wizard fetch — not persisted (stripped in buildPayload).
  Budget: null,
  // Inform-driven payout cadence (Program.payoutCadence, ADO 187057). Default
  // to IMMEDIATE — the most common case, and it means the reward step is valid
  // as soon as a budget is picked (no forced radio interaction).
  payoutCadence: "IMMEDIATE",
  // Program.payoutEndOffsetDays — only meaningful for the PROGRAM_END cadence.
  // Seeded to 30 so the offset field is pre-filled the first time the user
  // switches cadence to "After program ends".
  payoutEndOffsetDays: 30,
  status: "Incomplete",
  // No UI; omit on create → DB default false, which locks the roster after start.
  newUsersAfterProgramStart: true,
  payoutAmount: 10,
  ProgramElements: [],
  imageURL: null,
  imageAltText: "",
  MessageDefinitions: [],
  DetailTabs: [],
  // Not columns on the Program record — these persist through the
  // program-metric-config endpoint, like DetailTabs does through the tabs one.
  ProgramMetricRows: [],
  detailPageMetricCardId: null,
  // Summary card metrics, persisted through program-metric-config endpoint.
  summaryMetricCardId: null,
  ProgramSummaryMetricRows: [],
  builderFinished: false,
};

export default {
  name: "DynamicDataProgramBuilder",
  mixins: [permissionsMixin, clientFeatureFlagsMixin],
  components: {
    ModuleBuilder,
    SelectIfClaimProgram,
    DetailsStep,
    TimelineStep,
    ClaimTimelineStep,
    AudienceStep,
    RewardStep,
    ClaimFormStep,
    SummaryCardMetricsStep,
    SummaryCardMetricStepPreview,
    DetailPageMetricsStep,
    DetailPageMetricsStepPreview,
    ProgramMessagesStep,
    ConfirmStep,
    RuleSetBuilderIframe,
    ErrorDialogV2,
    Modal,
    RobinAnimation,
    Typography,
    SuccessStep,
    DetailPageTabsStep,
  },
  data() {
    return {
      program: { ...INITIAL_PROGRAM_STATE },
      image: null,
      imageObjectUrl: null,
      // UI-only draft toggle on the confirm step. Translated to `program.status`
      // on save; not a real field on the program record, so kept off `program`.
      previewMode: false,
      // Snapshot of the loaded program's status so we can preserve
      // Active/Archived when the user closes mid-edit without publishing.
      originalStatus: null,
      // Observability from the embedded RuleSet Builder (populated via its events).
      // The saved rule set's id is linked onto program.ruleSetId in persistRuleSet.
      ruleSetReady: false,
      ruleSetDirty: false,
      ruleSetValidation: null,
      // Sticky: set true the first time the rule step is reached and never reset, so
      // the iframe stays mounted (v-if) once loaded and only hidden (v-show) when the
      // user is on another step — preserving unsaved rule edits across navigation.
      hasReachedRuleStep: false,
      showCloseConfirm: false,
      showMessagesErrorConfirm: false,
      messagesErrorResolve: null,
      loadingProgram: false,
      savingProgram: false,
      claimProgramChosen: false,
      clientFeatureFlagsSettled: false,
      currentStepKey: null,
      initialStepKey: null,
      builderFinished: false,
      messageActiveSubstepKey: null,
      tabActiveSubstepKey: null,
      addedDefaultMessages: false,
      addedDefaultMetricRow: false,
      addedDefaultSummaryMetricRow: false,
      metricRowsLoadFailed: false,
      summaryMetricRowsLoadFailed: false,
      metricFieldOptions: [],
      metricOptionsError: false,
      metricOptionsLoaded: false,
      metricOptionsReason: null,
      metricProjection: null,
      budgets: [],
      loadingBudgets: false,
      claimForms: [],
      loadingClaimForms: false,
      canSelectBudget: false,
      programServiceV3: new ProgramServiceV3(),
      // `completed` tracks the latest validate() result. `everCompleted` is
      // sticky — once a step passes validation (or is inferred to have been
      // completed based on later-step state at load time) it stays true so we
      // can distinguish an Incomplete (was completed, now invalid) step from a
      // New (never completed) step.

      steps: buildSteps(),
    };
  },
  computed: {
    ...mapState(["globalClientId"]),
    isEditing() {
      const { clientId, programId } = this.$route.query;
      return (
        clientId !== undefined && clientId !== null && programId !== undefined && programId !== null
      );
    },
    // Create mode only: the intro holds a spinner until the claims flag picks intro or first step.
    awaitingClientFeatureFlags() {
      return (
        !this.isEditing &&
        !this.clientFeatureFlagsSettled &&
        !this.isClientFeatureFlagsResolvedFor(this.activeClientId)
      );
    },
    showIntro() {
      if (this.isEditing || this.claimProgramChosen) return false;
      return (
        this.awaitingClientFeatureFlags ||
        (this.isClientFeatureFlagsResolvedFor(this.activeClientId) &&
          this.isDynamicProgramBuilderClaimsEnabled())
      );
    },
    isClaimProgram() {
      return this.program.subtype === "claim";
    },
    selectedClaimFormId() {
      const element = (this.program.ProgramElements || []).find((el) => el.formId != null);
      return element ? Number(element.formId) : null;
    },
    selectedClaimFormName() {
      const form = this.claimForms.find((f) => Number(f.formId) === this.selectedClaimFormId);
      return form?.name || null;
    },
    activeClientId() {
      return this.$route.query.clientId ?? this.globalClientId;
    },
    imagePreviewUrl() {
      return this.program.imageURL || this.imageObjectUrl;
    },
    currentStepStatus() {
      const step = this.steps.find((s) => s.key === this.currentStepKey);
      return step ? this.deriveStepStatus(step) : null;
    },
    onLastStep() {
      return this.currentStepKey === this.steps[this.steps.length - 1].key;
    },
    // The program's start/end dates, mapped onto the rule set's validFrom/validTo.
    // The builder wants ISO 8601; the program holds the local `YYYY-MM-DDTHH:mm`
    // datetime-local value, so parse as local and emit ISO (null → undefined, so we
    // don't push an empty window into the builder).
    ruleSetValidFrom() {
      return this.toIsoOrUndefined(this.program.startDate);
    },
    ruleSetValidTo() {
      const { payoutCadence, payoutEndOffsetDays, ruleCalculationWindow, endDate } = this.program;
      const offsetDays = payoutCadence === "PROGRAM_END" ? Number(payoutEndOffsetDays) || 0 : 0;
      const payoutDate = getPayoutDate(endDate, offsetDays);

      if (ruleCalculationWindow && moment(ruleCalculationWindow).isValid()) {
        const cutoff = moment(ruleCalculationWindow);
        // ADO 191129: validTo must outlast the payout. Both builder steps now hold the
        // cutoff a full margin past the payout date (ADO 192799), so moment.max is a
        // no-op for anything saved since -- it stays for programs saved before that,
        // whose cutoff can still sit behind their payout date.
        return (payoutDate ? moment.max(cutoff, payoutDate) : cutoff).toISOString();
      }
      if (!payoutDate) return undefined;
      return payoutDate.add(RULE_CALCULATION_WINDOW_DAYS, "days").toISOString();
    },
    // One-shot seed for the embedded builder's blank create-mode bootstrap: a
    // >= / <= date-range pair covering the program's own start/end dates, so the
    // user only picks a Source Label and one field name per row. Plain YYYY-MM-DD,
    // not full ISO 8601 — daas-graph-update-service writes date-like properties
    // into Neo4j as bare JSON-derived strings, never coerced to a native Neo4j
    // Date/DateTime, so a longer ISO-datetime value would risk a lexicographic-
    // ordering mismatch against the shorter stored string at an exact boundary.
    // Only includes a row for whichever of startDate/endDate is actually set;
    // null (not []) when neither is set, so the builder stays exactly as blank as
    // it is today. NOT live-updated if the user revisits Timeline after this step
    // initializes — see RuleSetBuilderIframe's initialGlobalFilters prop.
    //
    // Gated on there being no RuleSet yet: an existing one owns its own Global
    // Filters (possibly intentionally different, or intentionally empty) and must
    // never be seeded over. The builder already enforces this itself — it only
    // applies the seed when it has no ruleSetId (RuleSetBuilderContainer.vue's
    // onMounted) — so this is belt-and-suspenders, and keeps us from putting a
    // meaningless seed on the wire. Deliberately keyed on ruleSetId rather than
    // isEditing: a previously-created program can legitimately have no rule set
    // yet (e.g. one created before this step existed), and that case SHOULD seed.
    initialGlobalFilters() {
      if (this.program.ruleSetId) return null;
      const start = this.toDateOnlyOrUndefined(this.program.startDate);
      const end = this.toDateOnlyOrUndefined(this.program.endDate);
      const filters = [];
      if (start) filters.push({ comparator: ">=", value: start });
      if (end) filters.push({ comparator: "<=", value: end });
      return filters.length ? [{ filters }] : null;
    },
    // Values we minted that the rule author has to reference verbatim inside the
    // rules. The builder shows these read-only with a copy button; it never
    // interprets or substitutes them.
    //
    // informProgramId is what scopes a :Payout node to THIS program. It has to be
    // carried through every step of the rule pipeline and end up in the final
    // rule's Group By, because that is what forms the node's `_nodeId` — a rule
    // that only projects it produces one node per participant shared across
    // programs, and the last ruleset to run overwrites the others' amounts.
    ruleSetConstants() {
      return this.program.informProgramId
        ? [{ name: "informProgramId", value: this.program.informProgramId }]
        : [];
    },
    programLength() {
      const { startDate, endDate } = this.program;
      if (!startDate || !endDate) return null;
      const start = moment(startDate);
      const end = moment(endDate);
      if (!start.isValid() || !end.isValid()) return null;
      return end.diff(start, "days") + 1;
    },
    // Single ordering for the messages list and its sidebar substeps. Depends on
    // programLength so editing the Timeline re-sorts end-anchored messages too.
    sortedMessages() {
      return sortMessagesBySendDate(this.program?.MessageDefinitions, this.programLength);
    },
    hasMessagePastProgramEnd() {
      if (this.programLength == null) return false;
      const messages = this.program?.MessageDefinitions || [];
      return messages.some((m) => {
        const days = m.numDaysAfterProgramStart ?? m.numDaysToProgramEnd;
        return days != null && Number(days) > this.programLength;
      });
    },
    activeSubstepKeyOverride() {
      if (this.currentStepKey === "program-messages") return this.messageActiveSubstepKey;
      if (this.currentStepKey === "detail-page-tabs") return this.tabActiveSubstepKey;
      return undefined;
    },
    // Once a program is Active or Archived, the Draft toggle disappears — you
    // can't demote a live program back to draft/preview through this UI.
    canEditDraftMode() {
      return this.originalStatus !== "Active" && this.originalStatus !== "Archived";
    },
    // Live or scheduled programs cannot change when participants are paid (ADO 190450).
    payoutLocked() {
      return this.originalStatus === "Active" || this.originalStatus === "Scheduled";
    },
    // Active and Scheduled stay live on save. Archived keeps its existing path.
    isLiveProgram() {
      return this.originalStatus === "Active" || this.originalStatus === "Scheduled";
    },
    // Publish button when we're actually going to publish; "Save" when the
    // Draft toggle is on (mirrors the Figma copy for the two states).
    lastStepLabel() {
      return this.previewMode ? "Save" : "Save & Publish";
    },
    metricSelectItems() {
      return toMetricSelectItems(this.metricFieldOptions);
    },
    currentBudgetLabel() {
      return this.budgetLabel(this.program.Budget);
    },
    currentBudgetBalance() {
      const balance = this.program.Budget?.budgetBalance;
      return balance == null ? null : Number(balance);
    },
    isDollarBudget() {
      return this.program.Budget?.awardId == 1;
    },
  },
  mounted() {
    this.loadClientFeatureFlags()
      .catch((e) => console.error("--- CLIENT FEATURE FLAGS LOAD FAILED ---", e))
      .finally(() => {
        this.clientFeatureFlagsSettled = true;
      });
    this.loadProgram();
    // Create mode: nothing to load, so a null id definitively means "none yet".
    // Edit mode mints inside loadProgram instead, once the saved value is known.
    if (!this.isEditing) {
      this.ensureInformProgramId();
      this.loadBudgets();
      this.loadClaimForms();
    }
  },
  beforeUnmount() {
    if (this.imageObjectUrl) {
      URL.revokeObjectURL(this.imageObjectUrl);
    }
  },
  methods: {
    mergeProgram(values) {
      this.program = { ...this.program, ...values };
    },
    onClaimProgramChoice(isClaimProgram) {
      if (isClaimProgram) this.mergeProgram({ subtype: "claim" });
      this.steps = buildSteps({ includeClaimForm: isClaimProgram });
      this.claimProgramChosen = true;
    },
    onClaimFormId(formId) {
      this.mergeProgram({
        ProgramElements: [
          {
            formId: Number(formId),
            status: "Active",
          },
        ],
      });
    },
    async loadClaimForms() {
      this.loadingClaimForms = true;
      try {
        const response = await FormService.getFormsV2(undefined, this.activeClientId, {
          limit: 300,
        });
        this.claimForms = (response?.result?.rows || []).filter(
          (form) => form.status !== "Deleted"
        );
      } catch (err) {
        this.$store.dispatch("showError", {
          message: "Error loading claim forms. Please try again.",
        });
        this.claimForms = [];
      } finally {
        this.loadingClaimForms = false;
      }
    },
    budgetLabel(budget) {
      return (
        budget?.budgetDisplayName ||
        budget?.budgetName ||
        budget?.BudgetName ||
        budget?.name ||
        null
      );
    },
    onRewardBudgetId(budgetId) {
      const fromList = this.budgets.find((b) => b.budgetId == budgetId);
      const budget = fromList || this.program.Budget;
      this.mergeProgram({ budgetId, Budget: budget || null });
    },
    // Mint the Inform program link if we don't have one yet.
    //
    // Has to happen before the rule-builder step (step 4), not at create (step 10):
    // the author references this id inside the rules themselves, so the builder
    // needs it at init. Idempotent, and only called where the current value is
    // known to be authoritative — see the two call sites.
    ensureInformProgramId() {
      if (!this.program.informProgramId) this.mergeProgram({ informProgramId: uuidv4() });
    },
    // Convert a datetime-local value to an ISO 8601 string for the rule set builder,
    // or undefined when unset/invalid. (buildPayload formats these differently for
    // the backend; the builder just needs something `new Date(...)` can parse.)
    toIsoOrUndefined(value) {
      return value && moment(value).isValid() ? moment(value).toISOString() : undefined;
    },
    // Same null/invalid handling as toIsoOrUndefined, but formats as a bare
    // YYYY-MM-DD date (no time/timezone) — see initialGlobalFilters above for why
    // the seeded filter values use a different format than ruleSetValidFrom/To.
    toDateOnlyOrUndefined(value) {
      return value && moment(value).isValid() ? moment(value).format("YYYY-MM-DD") : undefined;
    },
    toUtcDateTime(value) {
      return value && moment(value).isValid()
        ? moment(value).utc().format("YYYY-MM-DD HH:mm:ssZ")
        : null;
    },
    // Normalize datetimes to the UTC format the backend expects and strip
    // the in-memory File — the image is uploaded via a separate multipart
    // call after the program itself is saved. Mirrors ProgramWizard's shape.
    // `publishing` is true for Save & Publish and for a live close-save. Other saves write Incomplete.
    buildPayload({ publishing = false } = {}) {
      const payload = { ...this.program };
      // Force dynamic-data type: corrects legacy programs whose DB row had a null type on load.
      payload.type = "dynamic-data";
      payload.clientId = this.activeClientId;
      payload.status = this.resolveSaveStatus({ publishing });
      payload.startDate = this.toUtcDateTime(payload.startDate);
      payload.endDate = this.toUtcDateTime(payload.endDate);
      payload.ruleCalculationWindow = this.toUtcDateTime(payload.ruleCalculationWindow);
      // The v2 route persists these only for subtype "claim" and nulls them otherwise.
      const claimDates = [
        "claimsOpenDate",
        "claimsCloseDate",
        "cardActiveFrom",
        "cardArchiveAfter",
      ];
      for (const key of claimDates) {
        payload[key] = payload.subtype === "claim" ? this.toUtcDateTime(payload[key]) : null;
      }
      // Offset days only apply to the PROGRAM_END cadence — drop the value
      // when the user saves with IMMEDIATE so we don't persist a dormant
      // number that the reward step preserved across cadence toggles.
      if (payload.payoutCadence === "IMMEDIATE") {
        payload.payoutEndOffsetDays = null;
      }
      // Force on: DDPB has no toggle, and GET of older programs would otherwise persist false.
      payload.newUsersAfterProgramStart = true;
      payload.ProgramGroups = payload.ProgramGroups.map((g) => ({
        groupId: g.groupId,
      }));
      payload.MessageDefinitions = this.buildMessagePayloads(payload.MessageDefinitions);
      // Omit rather than null out, so an update can never clear an existing Inform link.
      if (!payload.informProgramId) delete payload.informProgramId;
      // Omit unset metric card ids so updates don't overwrite existing links with null.
      if (payload.detailPageMetricCardId == null) delete payload.detailPageMetricCardId;
      if (payload.summaryMetricCardId == null) delete payload.summaryMetricCardId;
      // Never send payout rules back. This builder has no payout-rule UI, so it
      // only ever round-trips whatever the GET returned — and the v2 update
      // route persists them for claim programs only (`if (claimProgram)`),
      // while validating them for every program type. So the array we send is
      // one the route will ignore, and validating it can only ever fail.
      //
      // It did: `amount` is DECIMAL(10,2), which mysql2 returns as the STRING
      // "0.00", and the route's check is
      // `!Number.isInteger(amount) && !isFloat(amount)` where `isFloat` is
      // `Number(n) === n && n % 1 !== 0`. A string fails both, so the save 400s
      // with "ProgramPayoutRules.amount:invalid float". It only appeared once a
      // program had been paid, because the INFORM_METRIC rule payouts create is
      // the first payout rule a dynamic-data program ever has.
      delete payload.ProgramPayoutRules;
      delete payload.Budget;
      return payload;
    },
    // Draft saves write Incomplete — except on an ended program, which keeps
    // Archived. Archived is a PAYABLE status for Inform-driven payouts
    // (microservice-programs PAYABLE_PROGRAM_STATUSES = ["Active", "Archived"]),
    // because /v2/program/cron/status archives a program at endDate, which is
    // payoutEndOffsetDays BEFORE a PROGRAM_END payout comes due. That cron only
    // ever moves Scheduled -> Active -> Archived and never reads Incomplete, so
    // nothing brings a program back: a no-change close-save on an ended program
    // silently cancelled every payout still owed. ADO 191128.
    //
    // Archived rather than Active — which is what this returned before 9ac56548 —
    // because Active un-archives an ended program until the next hourly status
    // run, showing participants a finished program as if it were still open.
    //
    // A publish resolves Active or Scheduled from the start date.
    resolveSaveStatus({ publishing }) {
      if (!publishing) return this.originalStatus === "Archived" ? "Archived" : "Incomplete";
      if (this.previewMode) return "Preview";
      const start = this.program.startDate ? moment(this.program.startDate) : null;
      const startsNowOrPast = start && start.isValid() && !start.isAfter(moment());
      return startsNowOrPast ? "Active" : "Scheduled";
    },

    buildMessagePayloads(messages) {
      return (messages || [])
        .filter((m) => this.messageFitsProgramLength(m))
        .map((m) => {
          const payload = {
            ...m,
            clientId: this.activeClientId,
            context: "program",
            subcontext: "dynamic-data-program-builder",
            contextResourceId: this.program.programId || m.contextResourceId,
            displayImageInCardPreview: false,
            displayOnPlatform: true,
            sendDashboard: true,
            sendEmail: true,
            sendSMS: true,
            sendPush: true,
          };
          // PATCH /v2/program treats missing `id` (or newMessage) as a create.
          if (m.newMessage) delete payload.id;
          return payload;
        });
    },

    // The endpoint is generated with `throw-on-extras`, so builder state has to
    // hold the shape the upsert accepts: drop the fields the API owns on the way
    // in, and the payload is then the rows themselves.
    toBuilderMetricRows(rows) {
      return (rows || []).map((row) => ({
        rowId: row.rowId,
        columnCount: row.columnCount,
        // Each column also arrives tagged with the rowId that owns it.
        columns: (row.columns || []).map((column) => {
          const builderColumn = { ...column };
          delete builderColumn.rowId;
          return builderColumn;
        }),
      }));
    },
    buildMetricRowPayloads(rows) {
      return (rows || []).map((row) => ({
        //negative rows are new rows
        rowId: row.rowId > 0 ? row.rowId : undefined,
        columnCount: row.columnCount,
        columns: (row.columns || [])
          // An unconfigured slot has nothing to persist, and one left stranded
          // past a shrunk columnCount is no longer rendered.
          .filter((column) => !!column.componentType && column.slotIndex < row.columnCount),
      }));
    },
    // Mirrors ProgramWizard's legacy save-time behavior: silently drop any
    // message whose send date falls outside the program window (i.e. the user
    // added the message when the program was longer, then shortened Timeline).
    // TODO(product): the drop is silent today — legacy has the same behavior.
    // Confirm whether we should instead (a) block Continue on the messages
    // step and open the offending row, (b) show a warning modal at save,
    // or (c) keep the silent filter. Same conversation applies to future
    // no-end / rolling-window program modes.
    messageFitsProgramLength(m) {
      if (this.programLength == null) return true;
      const days = m.numDaysAfterProgramStart ?? m.numDaysToProgramEnd;
      return days != null && Number(days) <= this.programLength;
    },
    injectDefaultMessages() {
      const toAdd = DEFAULT_MESSAGES.map((d) => ({ ...d }));
      this.mergeProgram({
        MessageDefinitions: [...this.program.MessageDefinitions, ...toAdd],
      });
      this.addedDefaultMessages = true;
    },
    // No internal latch — relies on the caller's addedDefaultMetricRow guard
    // (currentStepKey watcher). Differs from injectDefaultSummaryMetricRow below,
    // which also guards itself for direct-call idempotency.
    injectDefaultMetricRow() {
      this.mergeProgram({
        ProgramMetricRows: [...this.program.ProgramMetricRows, defaultMetricRow()],
      });
      this.addedDefaultMetricRow = true;
    },
    // Latches internally (unlike injectDefaultMetricRow) so a direct double-call
    // is also idempotent, not just caller-guarded.
    injectDefaultSummaryMetricRow() {
      if (this.addedDefaultSummaryMetricRow) return; // Latch to prevent double-injection
      // Only fill a genuinely empty step — an "incomplete" program with existing rows
      // just needs a configured metric, not another row stacked on top.
      if (this.program.ProgramSummaryMetricRows.length > 0) return;
      this.mergeProgram({
        ProgramSummaryMetricRows: [...this.program.ProgramSummaryMetricRows, defaultMetricRow()],
      });
      this.addedDefaultSummaryMetricRow = true;
    },
    async createProgram({ publishing } = {}) {
      // Backstop. ensureInformProgramId() has normally already run at mount, but a
      // save must never persist a dynamic program with no Inform link. Assigned
      // before the first await so a re-entrant save can't mint a second one.
      this.ensureInformProgramId();
      return await ProgramService.createProgramV2(this.buildPayload({ publishing }));
    },
    async updateProgram({ publishing } = {}) {
      const payload = this.buildPayload({ publishing });
      return await ProgramService.updateProgramV2(payload.programId, payload);
    },

    async uploadProgramImage(programId) {
      if (!this.image || !programId) return;
      return await ProgramService.uploadProgramImage(
        programId,
        this.image,
        this.activeClientId,
        this.program.imageAltText
      );
    },
    async loadProgram() {
      if (!this.isEditing) return;
      const { programId, clientId } = this.$route.query;
      this.loadingProgram = true;
      try {
        const loaded = await ProgramService.getProgramForWizardV2(
          programId,
          clientId,
          DDPB_CONTEXT
        );
        const programResponse = loaded?.rows?.[0];
        if (!programResponse) throw new Error("Program not found");

        for (const key of [
          "startDate",
          "endDate",
          "claimsOpenDate",
          "claimsCloseDate",
          "cardActiveFrom",
          "cardArchiveAfter",
          "ruleCalculationWindow",
        ]) {
          if (programResponse[key]) {
            programResponse[key] = moment(programResponse[key]).format(TIMELINE_VALUE_FORMAT);
          }
        }
        programResponse.ProgramGroups = (programResponse.ProgramGroups || []).map((g) => ({
          groupId: g.groupId,
        }));
        programResponse.imageURL = programResponse.imageURL || null;
        programResponse.imageAltText = programResponse.imageAltText || "";
        // Hydrate UI-only state from the loaded status before spreading into
        // `program` — the record itself has no previewMode / originalStatus.
        this.previewMode = programResponse.status === "Preview";
        this.originalStatus = programResponse.status ?? null;
        this.program = { ...INITIAL_PROGRAM_STATE, ...programResponse };
        this.steps = applySteps(this.steps, {
          isEditing: this.isEditing,
          isClaimProgram: this.isClaimProgram,
        });
        this.assignedBudgetId = programResponse.budgetId ?? null;
        this.assignedBudget = programResponse.Budget || null;
        // Only here, on the success path: a program saved before this column
        // existed has none and needs one, but minting after a FAILED load would
        // replace a saved id and orphan every :Payout node keyed to the old one.
        this.ensureInformProgramId();
        this.hydrateStepStateFromSupplemental(programResponse);
        // Messages live behind their own endpoint — not inline on the program
        // response — so fetch them separately. Groups can load in parallel.
        await Promise.all([
          this.loadAudienceGroupName(),
          this.loadMessages(),
          this.loadTabs(),
          this.loadDetailPageMetricRows(),
          this.loadSummaryMetricRows(),
          this.loadMetricLookups(),
          this.loadBudgets(),
          this.loadClaimForms(),
        ]);
      } catch (err) {
        console.error("Error loading program", err);
        this.$store.dispatch("showError", { message: "Error loading program" });
      } finally {
        this.loadingProgram = false;
        await this.$nextTick();
        for (const step of this.steps) {
          this.updateStepName(step.key);
        }
        await this.applyInitialStepRouting();
      }
    },

    hydrateStepStateFromSupplemental(programResponse) {
      const supp = (programResponse?.ProgramSupplementals ?? []).find(
        (s) => s.status === "Active" && s.key === DDPB_STATE_KEY
      );
      if (!supp?.value?.steps) return;
      this.steps = this.steps.map((step) => {
        const persisted = supp.value.steps[step.key];
        if (!persisted) return step;
        return {
          ...step,
          completed: !!persisted.completed,
          everCompleted: !!persisted.everCompleted,
        };
      });
    },
    async loadAudienceGroupName() {
      const groups = this.program?.ProgramGroups ?? [];

      if (groups.length === 0) return;

      try {
        // groupId 0 is the "Everyone" pseudo-group and doesn't exist on
        // the group service — strip it from the lookup payload.
        const groupIds = groups.map((g) => g.groupId).filter((id) => id !== 0);
        const response = await GroupService.getGroupsV2({
          filter: `groupId.isIn(${JSON.stringify(groupIds)}) && status =='Active'`,
          limit: groups.length,
          offset: 0,
          extract: "groupDisplayName,clientId,groupId,groupType",
        });
        this.program.ProgramGroups = response.result.rows.map((g) => ({
          groupId: g.groupId,
          groupDisplayName: g.groupDisplayName,
        }));
        if (groups.find((g) => g.groupId === 0)) {
          this.program.ProgramGroups.unshift({
            groupId: 0,
            groupDisplayName: "Everyone",
          });
        }
      } catch (err) {
        this.$store.dispatch("showError", {
          message: "Program loaded, but the audience couldn't be loaded. Please try again.",
        });
      }
    },
    async loadMessages() {
      const programId = this.program?.programId;
      if (!programId) return;
      try {
        const response = await CommunicationService.getMessageDefinitionsV2(
          { limit: 100 },
          null,
          programId
        );
        // Message bodies come back wrapped in the email HTML template — strip
        // the wrapper so the editor sees just the user's authored content.
        // Comms returns `messageId`; the program save contract keys on `id`.
        this.program.MessageDefinitions = (response?.result?.rows || []).map((m) => {
          const { messageId, ...rest } = m;
          return {
            ...rest,
            id: messageId,
            messageBody: getMessageBodyFromTemplate(m.messageBody),
            newMessage: false,
          };
        });
      } catch (err) {
        this.$store.dispatch("showError", {
          message: "Program loaded, but program messages couldn't be loaded. Please try again.",
        });
      }
    },
    async loadTabs() {
      const programId = this.program?.programId;
      if (!programId) return;
      try {
        const tabsResponse = await ProgramService.getTabsV3(programId, this.activeClientId);
        if (tabsResponse && tabsResponse.rows) {
          const loadedTabs = await Promise.all(
            tabsResponse.rows.map(async (tab) => {
              try {
                return {
                  ...tab,
                  bodyContent: (await loadEmbeddedContent(tab.bodyContent)) ?? tab.bodyContent,
                };
              } catch {
                return tab;
              }
            })
          );
          this.mergeProgram({ DetailTabs: loadedTabs });
        }
      } catch (err) {
        this.$store.dispatch("showError", {
          message: "Program loaded, but detail page tabs couldn't be loaded. Please try again.",
        });
      }
    },
    async loadMetricRowsConfig(cardId) {
      const programId = this.program?.programId;
      if (!programId) return [];
      const config = await this.programServiceV3.getProgramMetricConfig(
        programId,
        this.activeClientId,
        { cardId }
      );
      return this.toBuilderMetricRows(config?.rows ?? []);
    },
    async loadDetailPageMetricRows() {
      if (!this.program?.programId) return;
      try {
        const rows = await this.loadMetricRowsConfig(this.program.detailPageMetricCardId);
        if (rows.length) this.mergeProgram({ ProgramMetricRows: rows });
      } catch (err) {
        this.metricRowsLoadFailed = true;
        this.$store.dispatch("showError", {
          message: "Could not load program metrics, other program settings can be updated.",
        });
      }
    },
    async loadSummaryMetricRows() {
      if (!this.program?.programId) return; // truly new/unsaved — the "new"-status watcher handles it
      try {
        if (this.program.summaryMetricCardId != null) {
          const rows = await this.loadMetricRowsConfig(this.program.summaryMetricCardId);
          if (rows.length) this.mergeProgram({ ProgramSummaryMetricRows: rows });
        }
      } catch (err) {
        this.summaryMetricRowsLoadFailed = true;
        this.$store.dispatch("showError", {
          message: "Could not load summary metrics, other program settings can be updated.",
        });
        return; // don't paper over a real failure with a fake default row
      }
      // No card id, or the card came back with no rows — both mean "previous program,
      // never configured." Backfill the default row so the step doesn't look broken for
      // data that legitimately loaded empty, and restore completed if it passed before.
      if (this.program.ProgramSummaryMetricRows.length === 0) {
        this.injectDefaultSummaryMetricRow();
        const step = this.steps.find((s) => s.key === "summary-card-metrics");
        if (step?.everCompleted) step.completed = true;
      }
    },
    async loadMetricLookups() {
      await Promise.all([this.loadMetricOptions(), this.loadMetricProjections()]);
    },
    async loadMetricOptions() {
      const programId = this.program?.programId;
      if (!programId) return;
      this.metricOptionsError = false;
      this.metricOptionsReason = null;
      try {
        const result = await this.programServiceV3.getMetricOptions(programId, this.activeClientId);
        this.metricFieldOptions = result?.options ?? [];
        this.metricOptionsReason = result?.reason ?? null;
        this.metricOptionsLoaded = true;
      } catch (err) {
        this.metricFieldOptions = [];
        this.metricOptionsReason = null;
        this.metricOptionsError = true;
        this.metricOptionsLoaded = true;
        this.$store.dispatch("showError", {
          message: "Error loading metric options. Please try again.",
        });
      }
    },
    async loadMetricProjections() {
      const programId = this.program?.programId;
      if (!programId) return;
      try {
        const result = await this.programServiceV3.getMetricProjections(
          programId,
          this.activeClientId
        );
        this.metricProjection = result?.metric ?? null;
      } catch (err) {
        this.metricProjection = null;
        this.$store.dispatch("showError", {
          message: "Error loading metric preview. Please try again.",
        });
      }
    },
    canReadBudgets() {
      return this.hasPermission("budgets:read:budget");
    },
    appendAssignedBudget() {
      const budgetId = this.program.budgetId == null ? null : Number(this.program.budgetId);
      if (budgetId == null || Number.isNaN(budgetId)) return;
      if (this.budgets.some((b) => Number(b.budgetId) === budgetId)) return;
      const source = this.program.Budget || {};
      this.budgets = [
        ...this.budgets,
        {
          ...source,
          budgetId,
          budgetName: this.budgetLabel(source) || `Budget ${budgetId}`,
        },
      ];
    },
    async loadBudgets() {
      if (!this.canReadBudgets()) {
        this.canSelectBudget = false;
        this.budgets = [];
        this.appendAssignedBudget();
        return;
      }
      this.loadingBudgets = true;
      try {
        const response = await BudgetService.getBudgets(
          { lite: 1, clientId: this.activeClientId },
          2
        );
        this.budgets = Array.isArray(response) ? response : [];
        this.canSelectBudget = this.budgets.length > 0;
        this.appendAssignedBudget();
      } catch (err) {
        this.$store.dispatch("showError", {
          message: "Error loading budgets. Please try again.",
        });
        this.budgets = [];
        this.canSelectBudget = false;
        this.appendAssignedBudget();
      } finally {
        this.loadingBudgets = false;
      }
    },
    handleClose() {
      if (this.builderFinished || this.showIntro) {
        this.navigateToProgramAdmin();
        return;
      }
      this.showCloseConfirm = true;
    },
    navigateToProgramAdmin() {
      this.$router.push({ name: "programadmin" });
    },
    async handleDetailPageTabSave(programId) {
      if (!this.program.DetailTabs?.length) return true;

      const normalizedTabs = await Promise.all(
        this.program.DetailTabs.map(async (tab) => {
          const result = await uploadEmbeddedContent(tab.bodyContent, this.activeClientId);
          return { ...tab, bodyContent: result?.content ?? tab.bodyContent };
        })
      );
      const tabsForApi = normalizedTabs.map((tab) => ({
        tabId: tab.tabId > 0 ? tab.tabId : undefined,
        tabName: tab.tabName,
        title: tab.title,
        bodyContent: tab.bodyContent,
      }));

      const tabsResult = await ProgramService.upsertTabsV3(
        programId,
        tabsForApi,
        this.activeClientId
      );
      if (tabsResult?.rows) {
        const hydratedTabs = await Promise.all(
          tabsResult.rows.map(async (tab) => ({
            ...tab,
            bodyContent: (await loadEmbeddedContent(tab.bodyContent)) ?? tab.bodyContent,
          }))
        );
        this.mergeProgram({ DetailTabs: hydratedTabs });
      }
      return true;
    },
    async saveMetricRowsConfig(programId, rows, cardId) {
      return this.programServiceV3.upsertProgramMetricConfig(
        programId,
        this.buildMetricRowPayloads(rows),
        this.activeClientId,
        cardId
      );
    },
    // The v2 PATCH validates the whole program and rebuilds elements, groups and
    // attachments from the body, so a newly minted card id has to ride on the full
    // payload. Adopted before the write and kept if it fails: the rows already moved
    // onto this card, so a retry must target it, and the next save's PATCH carries the
    // id anyway. Rows are re-adopted too, or the next save re-creates them beside
    // themselves instead of updating them. Callbacks (not field names) merge the
    // result, so this stays reusable without reading `this.program` by string key.
    async adoptSavedMetricCard(config, currentCardId, publishing, applyCardId, applyRows) {
      if (config?.cardId != null && config.cardId !== currentCardId) {
        applyCardId(config.cardId);
        await this.updateProgram({ publishing });
      }
      if (config?.rows) {
        applyRows(this.toBuilderMetricRows(config.rows));
      }
    },
    // No early return on an empty list, unlike tabs: deleting every row is a real
    // edit, and a full-replace endpoint only sees it if the empty payload is sent.
    async handleDetailPageMetricSave(programId, { publishing = false } = {}) {
      // A failed load leaves ProgramMetricRows empty, and this endpoint replaces
      // in full — saving now would delete the config the fetch couldn't read.
      if (this.metricRowsLoadFailed) return true;

      const currentCardId = this.program.detailPageMetricCardId;
      const config = await this.saveMetricRowsConfig(
        programId,
        this.program.ProgramMetricRows,
        currentCardId
      );
      await this.adoptSavedMetricCard(
        config,
        currentCardId,
        publishing,
        (cardId) => this.mergeProgram({ detailPageMetricCardId: cardId }),
        (rows) => this.mergeProgram({ ProgramMetricRows: rows })
      );
      return true;
    },
    async handleSummaryMetricSave(programId, { publishing = false } = {}) {
      // A failed load leaves ProgramSummaryMetricRows empty, and this endpoint replaces
      // in full — saving now would delete the config the fetch couldn't read.
      if (this.summaryMetricRowsLoadFailed) return true;

      // Nothing configured and nothing saved before: skip, rather than minting a permanent
      // empty orphan card on every unrelated save of a pre-existing program (AC requires
      // "no program metric rows" for these programs, not an empty card shell).
      if (
        this.program.summaryMetricCardId == null &&
        this.program.ProgramSummaryMetricRows.length === 0
      ) {
        return true;
      }

      const currentCardId = this.program.summaryMetricCardId;
      const config = await this.saveMetricRowsConfig(
        programId,
        this.program.ProgramSummaryMetricRows,
        currentCardId
      );
      await this.adoptSavedMetricCard(
        config,
        currentCardId,
        publishing,
        (cardId) => this.mergeProgram({ summaryMetricCardId: cardId }),
        (rows) => this.mergeProgram({ ProgramSummaryMetricRows: rows })
      );
      return true;
    },
    async handleSave({ close = false, publishing = false } = {}) {
      // Resolve up-front so the SuccessStep's "saved" vs "published" copy
      // matches the payload we're actually sending.
      const status = this.resolveSaveStatus({ publishing });

      // === 1. CREATE/UPDATE PROGRAM ===
      let programId;
      if (this.isEditing || this.program.programId) {
        await this.updateProgram({ publishing });
        programId = this.program.programId;
      } else {
        const created = await this.createProgram({ publishing });
        programId = created?.programId;
        if (programId) this.mergeProgram({ programId });
      }

      if (!programId) {
        this.$store.dispatch("showError", {
          message: "Failed to save program. Please try again.",
        });
        return false;
      }

      // Adopt persisted message ids so the next save updates instead of duplicating.
      await this.loadMessages();

      await this.loadMetricLookups();

      await this.uploadProgramImage(programId);

      // === 2. SAVE TABS ===
      try {
        await this.handleDetailPageTabSave(programId);
      } catch {
        this.$store.dispatch("showError", {
          message: "Program saved, but tab changes could not be saved. Please try again.",
        });
        return false;
      }

      // === 3. SAVE PROGRESS METRICS ===
      try {
        await this.handleDetailPageMetricSave(programId, { publishing });
      } catch {
        this.$store.dispatch("showError", {
          message:
            "Program saved, but detail page metric changes could not be saved. Please try again.",
        });
        return false;
      }

      // === 4. SAVE SUMMARY METRICS ===
      try {
        await this.handleSummaryMetricSave(programId, { publishing });
      } catch {
        this.$store.dispatch("showError", {
          message:
            "Program saved, but summary metric changes could not be saved. Please try again.",
        });
        return false;
      }

      this.mergeProgram({ status });
      await this.persistStepState(programId);
      if (close) this.navigateToProgramAdmin();
      return true;
    },
    // Best-effort: the program has already been saved by the time this runs,
    // so a failed supplemental write is logged and swallowed rather than
    // surfaced to the user or aborting the save flow.
    async persistStepState(programId) {
      if (!programId) return;
      try {
        const stepState = this.steps.reduce((acc, s) => {
          acc[s.key] = {
            completed: !!s.completed,
            everCompleted: !!s.everCompleted,
          };
          return acc;
        }, {});
        await this.programServiceV3.upsertProgramSupplemental({
          programId,
          key: DDPB_STATE_KEY,
          value: { steps: stepState },
        });
      } catch (e) {
        console.warn("--- FAILED TO PERSIST DDPB STATE ---", e);
      }
    },
    handleDiscardAndClose() {
      this.$router.push({ name: "programadmin" });
    },
    async handleCloseConfirmSave() {
      // Only persist the rule set on its step, where the builder can show validation errors.
      if (this.currentStepKey === "dynamic-rule-builder" && this.$refs.ruleBuilder) {
        const ok = await this.persistRuleSet();
        if (!ok) {
          if (this.isLiveProgram) this.rejectLivePublish();
          return;
        }
      }

      if (this.hasMessagePastProgramEnd) {
        const proceed = await this.confirmDiscardInvalidMessages();
        if (!proceed) return;
      }
      const currentStepValid = await this.validateStep(this.currentStepKey);
      if (this.isLiveProgram && !currentStepValid) {
        this.rejectLivePublish();
        return;
      }
      try {
        this.savingProgram = true;
        const saved = await this.handleSave({ close: true, publishing: this.isLiveProgram });
        if (!saved) return;
      } catch (e) {
        this.$store.dispatch("showError", {
          message: "Error saving program, please try again",
        });
      } finally {
        this.savingProgram = false;
      }
    },
    rejectLivePublish() {
      this.showCloseConfirm = false;
      this.$store.dispatch("showError", {
        message: "Resolve the errors on this step before saving.",
      });
    },
    // Promise-based gate for the "You have messages with errors" dialog.
    // Resolves true when the user picks "Discard and Continue", false on
    // "Stay and fix". Callers await this before proceeding with save.
    confirmDiscardInvalidMessages() {
      this.showMessagesErrorConfirm = true;
      return new Promise((resolve) => {
        this.messagesErrorResolve = resolve;
      });
    },
    handleConfirmDiscardInvalidMessages() {
      this.messagesErrorResolve?.(true);
      this.messagesErrorResolve = null;
    },
    handleStayAndFix() {
      this.messagesErrorResolve?.(false);
      this.messagesErrorResolve = null;
    },
    async validateStep(stepKey) {
      const step = this.steps.find((s) => s.key === stepKey);
      let isValid = false;
      switch (stepKey) {
        case "details":
          isValid = this.$refs.detailsStep?.validate() ?? false;
          break;
        case "timeline":
          isValid = this.$refs.timelineStep?.validate() ?? false;
          break;
        case "audience":
          isValid = this.$refs.audienceStep?.validate() ?? false;
          break;
        case "dynamic-rule-builder":
          // Complete once the rule set has been saved to DaaS and its id linked to
          // the program. The actual validate + save happens on Next (persistRuleSet);
          // this branch only reports completion, because validateStep also runs on
          // load and back-navigation where we must not trigger a save.
          isValid = !!this.program.ruleSetId;
          break;
        case "reward":
          isValid = this.$refs.rewardStep?.validate() ?? false;
          break;
        case "claim-form":
          isValid = this.$refs.claimFormStep?.validate() ?? false;
          break;
        case "summary-card-metrics":
          isValid = this.$refs.summaryCardMetricsStep?.validate() ?? false;
          break;
        case "confirm":
          // Visit-to-complete step: everCompleted is the source of truth,
          // set by the currentStepKey watcher on first landing and hydrated
          // from the ProgramSupplemental row on load.
          isValid = !!step?.everCompleted;
          break;
        case "detail-page-metrics":
          isValid = this.$refs.detailPageMetricsStep?.validate() ?? false;
          break;
        case "detail-page-tabs":
          isValid = this.$refs.tabsStep?.validate() ?? false;
          break;
        case "program-messages":
          // Optional step: earns "complete" once the immediately previous step
          // (detail-page-tabs) is completed, so users can arrive without adding a message.
          {
            const detailPageTabsStep = this.steps.find((s) => s.key === "detail-page-tabs");
            isValid = !!detailPageTabsStep?.completed;
          }
          break;
        default:
          return false;
      }
      if (step) {
        step.completed = isValid;
        if (isValid) step.everCompleted = true;
      }
      return isValid;
    },
    deriveStepStatus(step) {
      if (!step) return null;
      if (step.completed && step.everCompleted) return "completed";
      if (!step.completed && step.everCompleted) return "incomplete";
      return "new";
    },
    async applyInitialStepRouting() {
      const targetIdx = this.steps.findIndex((s) => this.deriveStepStatus(s) !== "completed");
      const target = targetIdx === -1 ? this.steps[0] : this.steps[targetIdx];
      this.initialStepKey = target?.key ?? null;
      if (target && this.deriveStepStatus(target) === "incomplete") {
        // show error state for initial step if it's incomplete
        await this.validateStep(target.key);
      }
    },
    // Validate + save the embedded rule set to inform (which persists it to DaaS)
    // and link the returned id onto the program. Returns false — leaving the user on
    // the step — when the builder isn't ready, validation fails, or the save errors.
    // We don't toast on failure: the builder surfaces the error itself (its
    // global-error banner + per-field errors); returning false blocks the advance/close.
    async persistRuleSet() {
      const builder = this.$refs.ruleBuilder;
      if (!builder) return false;
      try {
        const result = await builder.save();
        if (!result || !result.ok) {
          this.ruleSetValidation = (result && result.validation) || null;
          return false;
        }
        this.mergeProgram({ ruleSetId: result.ruleSetId });
        await this.loadMetricLookups();
        return true;
      } catch (e) {
        console.error("[DynamicDataProgramBuilder] rule set save failed", e);
        return false;
      }
    },
    async handleNext(stepKey) {
      // The rule-builder step has no Publish button — advancing is what saves the
      // rule set and captures its id (linked onto the program below).
      if (stepKey === "dynamic-rule-builder" && !(await this.persistRuleSet())) {
        return false;
      }
      const isValid = await this.validateStep(stepKey);
      if (!isValid) {
        return false;
      }
      if (this.onLastStep) {
        if (this.hasMessagePastProgramEnd) {
          const proceed = await this.confirmDiscardInvalidMessages();
          if (!proceed) return false;
        }
        try {
          const saved = await this.handleSave({ publishing: true });
          if (!saved) return false;
        } catch (e) {
          this.$store.dispatch("showError", {
            message: "Error publishing program, please try again",
          });
          return false;
        }
      }
      return true;
    },
    async canLeaveStep() {
      if (this.currentStepStatus === "new") return true;
      await this.validateStep(this.currentStepKey);
      if (this.currentStepStatus !== "incomplete") return true;
      this.$store.dispatch("showError", {
        message: "Please resolve errors before navigating to a different step",
      });
      return false;
    },
    handleSubstepClick({ stepKey, substepKey }) {
      if (!substepKey) return;
      if (stepKey === "program-messages") {
        const id = Number(String(substepKey).replace(/^message-/, ""));
        if (Number.isNaN(id)) return;
        this.messageActiveSubstepKey = substepKey;
        this.$refs.messagesStep?.openMessageById(id);
        return;
      }
      if (stepKey === "detail-page-tabs") {
        const id = Number(String(substepKey).replace(/^tab-/, ""));
        if (Number.isNaN(id)) return;
        this.tabActiveSubstepKey = substepKey;
        this.$refs.tabsStep?.openTabById(id);
        return;
      }
    },
    updateStepName(stepKey) {
      const step = this.steps.find((s) => s.key === stepKey);
      if (!step) return;
      switch (step.key) {
        case "details":
          step.label = this.program.name || "Program Name";
          break;
        case "timeline": {
          const start = this.program.startDate
            ? moment(this.program.startDate).format("MM/DD/YY")
            : "";
          const end = this.program.endDate ? moment(this.program.endDate).format("MM/DD/YY") : "";
          step.label = start && end ? `${start} - ${end}` : "Timeline";
          break;
        }
        case "audience": {
          const groups = this.program.ProgramGroups;
          if (groups.length === 0) {
            step.label = "Audience";
          } else if (groups.length === 1) {
            step.label = groups[0].groupId === 0 ? "Everyone" : groups[0].groupDisplayName;
          } else {
            step.label = `Groups: ${groups.length} selected`;
          }
          break;
        }
        case "dynamic-rule-builder":
          step.label = "Dynamic rule";
          break;
        case "reward":
          step.label = this.currentBudgetLabel || "Reward";
          break;
        case "claim-form":
          step.label = this.selectedClaimFormName || "Claim form";
          break;
        case "program-messages": {
          const messages = this.program?.MessageDefinitions || [];
          step.label =
            messages.length > 0 ? `Program messages (${messages.length})` : "Program messages";
          break;
        }
        case "detail-page-tabs": {
          const tabs = this.program?.DetailTabs || [];
          step.label = tabs.length > 0 ? `Detail page tabs (${tabs.length})` : "Detail page tabs";
          break;
        }
        default:
          break;
      }
    },
  },
  watch: {
    image(newFile) {
      if (this.imageObjectUrl) {
        URL.revokeObjectURL(this.imageObjectUrl);
        this.imageObjectUrl = null;
      }
      if (newFile) {
        this.imageObjectUrl = URL.createObjectURL(newFile);
      }
    },
    program: {
      immediate: false,
      handler() {
        this.updateStepName(this.currentStepKey);
      },
    },
    // Once the rule step is reached, keep the iframe mounted for the rest of the
    // session (see the v-if/v-show on the rule step) so navigation doesn't drop edits.
    async currentStepKey(key, oldKey) {
      // Capture the outgoing step's validation state so persisted flags reflect
      // every transition (Next / Back / sidebar), not just forward moves.
      if (oldKey && oldKey !== key) {
        await this.validateStep(oldKey);
      }
      if (key === "dynamic-rule-builder") this.hasReachedRuleStep = true;
      if (
        key === "program-messages" &&
        this.currentStepStatus === "new" &&
        !this.addedDefaultMessages
      ) {
        this.injectDefaultMessages();
      }
      if (
        key === "detail-page-metrics" &&
        this.currentStepStatus === "new" &&
        !this.addedDefaultMetricRow
      ) {
        this.injectDefaultMetricRow();
      }
      if (
        key === "summary-card-metrics" &&
        this.currentStepStatus === "new" &&
        !this.addedDefaultSummaryMetricRow
      ) {
        this.injectDefaultSummaryMetricRow();
      }
      // Visit-to-complete: these steps have no intrinsic validation criterion
      // (0 messages is legitimate; confirm is informational). Landing on the step is
      // the completion signal and everCompleted persists via the supplemental across sessions.
      if (key === "program-messages" || key === "confirm") {
        const step = this.steps.find((s) => s.key === key);
        if (step && !step.everCompleted) {
          step.completed = true;
          step.everCompleted = true;
        }
      }
    },
    // Keep the tabs step's substeps in sync with DetailTabs.
    // Add/edit/delete all replace this array wholesale, so a shallow watcher
    // is enough. Immediate so an edit-mode load populates substeps as soon
    // as getTabsV3() resolves.
    "program.DetailTabs": {
      immediate: true,
      handler(tabs) {
        const step = this.steps.find((s) => s.key === "detail-page-tabs");
        if (!step) return;
        step.substeps = (tabs || []).map((t) => ({
          key: `tab-${t.tabId}`,
          label: t.tabName,
          completed: true,
        }));
      },
    },
    // Keep the messages step's substeps in sync with the ordered messages, so the
    // sidebar reflects both content and chronology. Immediate so an edit-mode load
    // populates substeps as soon as loadMessages() resolves.
    sortedMessages: {
      immediate: true,
      handler(messages) {
        const step = this.steps.find((s) => s.key === "program-messages");
        if (!step) return;
        step.substeps = (messages || []).map((m) => ({
          key: `message-${m.id}`,
          label: m.messageSubject,
          completed: true,
        }));
      },
    },
    hasMessagePastProgramEnd: {
      immediate: true,
      handler(hasError) {
        const step = this.steps.find((s) => s.key === "program-messages");
        if (step) step.error = hasError;
      },
    },
    showMessagesErrorConfirm(open) {
      if (!open && this.messagesErrorResolve) {
        this.messagesErrorResolve(false);
        this.messagesErrorResolve = null;
      }
    },
  },
};
</script>

<style scoped>
/* ModuleBuilder's step card is content-sized, so give the embedded rule builder a
   fixed height to fill the visible step area. The offset accounts for the non-compact
   ModuleBuilder chrome (64px toolbar + Back/Continue rows + padding); the builder
   scrolls internally past this. Scoped to this step only — ModuleBuilder is untouched. */
.dynamic-rule-builder-step {
  height: calc(100vh - 260px);
  min-height: 400px;
}
</style>
